import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { shopService } from '../../services/api'

const initialState = {
  products: [],
  categories: [],
  loading: false,
  error: null,
  filters: {
    search: '',
    category_id: null,
    product_type: null,
    product_kind: null,
    per_page: 15,
    page: 1,
    priceRange: [0, 2000],
    sortBy: 'name'
  },
  pagination: {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  },
  currency: 'pt_BR', // Detectado automaticamente pela API
  currentRequestId: undefined
}

// Thunks
export const fetchCatalog = createAsyncThunk(
  'shop/fetchCatalog',
  async (filters = {}, { rejectWithValue }) => {
    try {
      // Filtrar parâmetros vazios e não suportados pela API
      const apiFilters = {}
      
      if (filters.per_page) apiFilters.per_page = filters.per_page
      if (filters.page) apiFilters.page = filters.page
      if (filters.category_id && filters.category_id !== 'all') apiFilters.category_id = filters.category_id
      if (filters.product_type) apiFilters.product_type = filters.product_type
      if (filters.product_kind) apiFilters.product_kind = filters.product_kind
      if (filters.search) apiFilters.search = filters.search
      if (filters.sortBy) apiFilters.sortBy = filters.sortBy
      
      const response = await shopService.getCatalog(apiFilters)
      return response.data.obj
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch catalog')
    }
  }
)

const shopSlice = createSlice({
  name: 'shop',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {
        search: '',
        category_id: null,
        product_type: null,
        product_kind: null,
        per_page: 15,
        page: 1,
        priceRange: [0, 2000],
        sortBy: 'name'
      }
    },
    setCurrency: (state, action) => {
      state.currency = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCatalog.pending, (state, action) => {
        state.loading = true
        state.error = null
        // Guarda o id da requisição mais recente — se filtros mudarem
        // rápido (ex: trocar "Tipo de Produto" logo após o load inicial),
        // pode haver mais de um fetchCatalog em voo ao mesmo tempo. Sem
        // isso, o resultado de uma requisição antiga que resolve depois
        // sobrescreve o da mais nova, fazendo o filtro "não fazer nada".
        state.currentRequestId = action.meta.requestId
      })
      .addCase(fetchCatalog.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return
        state.loading = false
        state.products = action.payload.products || []
        state.categories = action.payload.categories || []
        state.pagination = action.payload.pagination || state.pagination
        // Atualizar currency se vier no payload
        if (action.payload.currency) {
          state.currency = action.payload.currency
        }
      })
      .addCase(fetchCatalog.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return
        state.loading = false
        state.error = action.payload
      })
  }
})

export const { clearError, setFilters, clearFilters, setCurrency } = shopSlice.actions

// Selectors
export const selectShopProducts = (state) => state.shop?.products || []
export const selectShopCategories = (state) => state.shop?.categories || []
export const selectShopLoading = (state) => state.shop?.loading || false
export const selectShopError = (state) => state.shop?.error || null
export const selectShopFilters = (state) => state.shop?.filters || initialState.filters
export const selectShopPagination = (state) => state.shop?.pagination || initialState.pagination
export const selectShopCurrency = (state) => state.shop?.currency || 'pt_BR'

// Selector para produtos filtrados (incluindo filtro de preço local)
export const selectFilteredShopProducts = (state) => {
  const products = state.shop?.products || []
  const { priceRange } = state.shop?.filters || { priceRange: [0, 2000] }
  
  if (!priceRange || priceRange.length !== 2) return products
  
  const [minPrice, maxPrice] = priceRange
  
  return products.filter(product => {
    const price = product.price || 0
    return price >= minPrice && price <= maxPrice
  })
}

// Selector para produto específico
export const selectShopProductById = (productId) => (state) => {
  const products = state.shop?.products || []
  return products.find(product => product.id === productId)
}

// Selector para categorias como opções de select
export const selectCategoryOptions = (state) => {
  const categories = state.shop?.categories || []
  const options = [{ value: 'all', label: 'Todas as Categorias' }]
  
  categories.forEach(category => {
    options.push({
      value: category.id,
      label: category.namePt || category.nameEn || category.nameEs || category.name
    })
  })
  
  return options
}

export default shopSlice.reducer